import type { AIModel, ConfigFormState } from './types'

/** The two model slots of a use-case config; each owns one JSON textarea. */
export type ModelRole = 'primary' | 'fallback'

/** What the fallback Select stores for "no fallback" — the save path omits the field on it. */
export const NO_FALLBACK = 'none'

/**
 * Apply a model choice to the form. The slot's JSON is always replaced by the new model's
 * defaults: parameter names are provider-specific (`maxTokens` vs Google's `maxOutputTokens`),
 * so carrying the previous model's keys across produces settings the client silently ignores.
 */
export function applyModelSelection(
  formData: ConfigFormState,
  role: ModelRole,
  modelId: string,
  models: AIModel[],
): ConfigFormState {
  const model = models.find((m) => m.id === modelId)

  switch (role) {
    case 'fallback':
      return {
        ...formData,
        fallback_model_id: modelId,
        model_config: {
          ...formData.model_config,
          fallback_json: modelId === NO_FALLBACK ? '' : modelDefaultJson(model),
        },
      }

    case 'primary': {
      // Promoting the current fallback to primary would leave an id the fallback Select filters
      // out of its own options: a blank trigger that still saves the orphaned model.
      const dropsFallback = formData.fallback_model_id === modelId
      return {
        ...formData,
        primary_model_id: modelId,
        fallback_model_id: dropsFallback ? NO_FALLBACK : formData.fallback_model_id,
        model_config: {
          ...formData.model_config,
          primary_json: modelDefaultJson(model),
          ...(dropsFallback ? { fallback_json: '' } : {}),
        },
      }
    }

    default: {
      const exhaustiveRole: never = role
      return exhaustiveRole
    }
  }
}

/**
 * A model's own default parameters, ready for the JSON textarea. Empty for a model that ships
 * none: `JSON.parse(json || '{}')` on save reads that as "no override".
 */
export function modelDefaultJson(model?: AIModel): string {
  if (!model?.config || Object.keys(model.config).length === 0) return ''
  return JSON.stringify(model.config, null, 2)
}
